import React, { useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styled from '@emotion/styled';
import { Card } from 'components';
import device from './device';
import Colors from './Theme';
import { Spin } from 'antd';
import isMobile from './isMobile';
import { Link } from 'react-router-dom';
import ExternalLink from 'components/ExternalLink/ExternalLink';

const ArrowButton = styled.button`
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  background: ${Colors.white};
  --tw-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15), 0px 4px 10px rgba(0, 0, 0, 0.1);
  --tw-shadow-color: rgba(
    0,
    0,
    0,
    0.2
  ); /* Replace with your desired shadow color */
  --tw-shadow-colored: 0px 1px 2px var(--tw-shadow-color),
    0px 4px 10px var(--tw-shadow-color);

  box-shadow: var(--tw-shadow);
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  background: ${Colors.white};
  box-shadow: 0 16px 24px 2px rgba(0, 0, 0, 0.14),
    0 6px 30px 5px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 30px 40px;
  @media ${device.allMobile}, ${device.tablet} {
    padding: 0;
    background: ${Colors.transparent};
    box-shadow: none;
    border-radius: 0;
  }
  .card {
    width: 32%;
    display: inline-block;
    text-align: center;
    margin-bottom: 20px;

    @media ${device.allMobile} {
      width: 90%;
      margin: auto;
      margin-bottom: 20px;
    }
    @media ${device.minLaptop} {
      width: 48%;
      margin: auto;
      margin-bottom: 20px;
      margin-top: 0;
    }
    &:nth-of-type(3n + 2) {
      // margin: 0 15px 20px;
      @media ${device.tablet} {
        margin: 0 14px 20px;
      }
      @media ${device.allMobile} {
        width: 90%;
        margin: auto;
        margin-bottom: 20px;
      }
      @media ${device.minLaptop} {
        margin: auto;
        margin-top: 0;
        margin-bottom: 20px;
      }
    }
  }
`;

const Heading = styled.h2`
  text-align: left;
  font-size: 30px;
  color: #95bd51;
  margin: 0;
`;

const ShowMore = styled.button`
  font-weight: bold;
  background: rgb(149, 189, 81);
  border: 1px solid rgb(149, 189, 81);
  color: rgb(255, 255, 255);
  text-align: center;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 20px;
  padding: 15px 20px;
  min-width: 200px;
  font-size: 22px;
`;

const AppSlider = ({
  isSuccessful,
  projects,
  heading,
  showMoreHandle,
  isShowMore,
  isLoading,
  chunkSize = 3,
  isMain,
}) => {
  const sliderRef = useRef();
  if ((!projects || !projects.length) && isLoading)
    return <Spin spinning={isLoading} />;
  if (!projects || !projects.length)
    return (
      <div>
        <h1 style={{ marginBottom: 20, marginTop: 40 }}>{heading}</h1>
        <div>No project found</div>
      </div>
    );
  return (
    <Spin spinning={isLoading}>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile() ? 'column' : 'row',
          gap: 8,
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 40,
          marginBottom: 10,
          paddingRight: isMobile() ? 0 : 10,
        }}
      >
        <Heading>{heading}</Heading>

        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!isMobile() && (
            <>
              <ArrowButton onClick={() => sliderRef.current?.slickPrev()}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                  class="w-5"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </ArrowButton>
              <ArrowButton onClick={() => sliderRef.current?.slickNext()}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                  class="w-5"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </ArrowButton>
            </>
          )}
        </div>
      </div>

      {isMobile() ? (
        <div style={{ width: '100%' }} className="projects-slider">
          <Slider
            className="carousel-container"
            ref={sliderRef}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            arrows={false}
            swipe
            centerMode
            infinite
          >
            {projects.map((project, index) => (
              <div
                style={{ height: '100%' }}
                key={`${JSON.stringify(project)}-${index}`}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                >
                  <Card isSuccessful={isSuccessful} project={project} />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          <Slider
            className="carousel-container big-projects-slider"
            ref={sliderRef}
            speed={500}
            slidesToShow={chunkSize}
            slidesToScroll={chunkSize}
            arrows={false}
          >
            {/* {chunkArray(projects, chunkSize).map((px3, index) => (
              <div key={index}>
                <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                  {px3.map((project, index) => (
                    <Card
                      isSuccessful={isSuccessful}
                      project={project}
                      key={`${JSON.stringify(project)}-${index}`}
                    />
                  ))}
                </div>
              </div>
            ))} */}
            {projects.map((project, index) => (
              <div
                style={{ height: '100%' }}
                key={`${JSON.stringify(project)}-${index}`}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                >
                  <Card isSuccessful={isSuccessful} project={project} />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
      <div style={{ display: 'flex', marginTop: 10, justifyContent: 'center' }}>
        {isShowMore && (
          <>
            {isMain ? (
              <ExternalLink href="/projects">
                <ShowMore>Mehr anzeigen</ShowMore>
              </ExternalLink>
            ) : (
              <ShowMore onClick={showMoreHandle}>Mehr anzeigen</ShowMore>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default AppSlider;

function chunkArray(array, chunkSize = 3) {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
}
